import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUp, Disc3, Music2, Plus, Radio } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { ErrorState, LoadingState, Modal } from "@/components/app/AppUI";
import { BrandLogo } from "@/components/BrandLogo";
import { guestRepository } from "@/data/repositories";

export const Route = createFileRoute("/evento/$token/canciones")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Canciones del evento · SUSIEDAD" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: GuestSongsPage,
});

function GuestSongsPage() {
  const { token } = Route.useParams();
  const [sessionToken] = useState(() => guestRepository.sessionToken());
  const [proposalOpen, setProposalOpen] = useState(false);
  const client = useQueryClient();
  const event = useQuery({
    queryKey: ["guest-event", token],
    queryFn: () => guestRepository.event(token, sessionToken),
    retry: false,
  });
  const songs = useQuery({
    queryKey: ["guest-songs", token],
    queryFn: () => guestRepository.songs(token, sessionToken),
    enabled: Boolean(event.data),
    refetchInterval: 4000,
  });
  const vote = useMutation({
    mutationFn: (songId: string) => guestRepository.toggleVote(token, sessionToken, songId),
    onSuccess: () => client.invalidateQueries({ queryKey: ["guest-songs", token] }),
    onError: (error) => toast.error(error.message),
  });
  if (event.isLoading)
    return (
      <div className="min-h-screen bg-onyx text-bone">
        <LoadingState label="Abriendo la pista" />
      </div>
    );
  if (event.error) return <ErrorState message={event.error.message} />;
  if (!event.data) return <Expired />;
  const record = event.data;

  return (
    <main className="min-h-screen bg-onyx text-bone">
      <div className="mx-auto max-w-2xl px-4 pb-24 pt-5 sm:px-6">
        <header className="flex items-center justify-between border-b border-bone/15 pb-5">
          <div className="flex items-center gap-3">
            <BrandLogo variant="monogram" className="h-8 w-auto" />
            <div>
              <BrandLogo className="h-4 w-auto" />
              <p className="font-mono text-[8px] uppercase tracking-[0.2em] text-bone/45">
                Guest frequency
              </p>
            </div>
          </div>
          <span className="inline-flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.18em] text-neon">
            <Radio className="size-3 animate-pulse" /> Live
          </span>
        </header>
        <section className="relative overflow-hidden py-12">
          <div className="absolute -right-24 top-4 size-64 rounded-full border border-neon/20" />
          <p className="eyebrow !text-neon">{record.venue ?? "Evento privado"}</p>
          <h1 className="relative mt-4 font-display text-5xl leading-[0.9] sm:text-7xl">
            ¿QUÉ CANCIÓN NO PUEDE FALTAR?
          </h1>
          <p className="relative mt-5 max-w-lg text-lg text-bone/60">
            Propón la tuya y vota las favoritas. SUSIEDAD revisa cada propuesta antes de publicarla.
          </p>
          {record.proposals_open && (
            <button className="btn-primary mt-7" onClick={() => setProposalOpen(true)}>
              <Plus className="size-4" /> Proponer canción
            </button>
          )}
        </section>
        <section>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <p className="eyebrow !text-bone/45">Ranking · {record.title}</p>
              <h2 className="mt-1 font-display text-2xl">LAS MÁS VOTADAS</h2>
            </div>
            <Disc3 className="size-6 text-neon" />
          </div>
          {songs.isLoading ? (
            <LoadingState label="Actualizando ranking" />
          ) : (songs.data ?? []).length === 0 ? (
            <div className="border border-bone/15 p-8 text-center">
              <Music2 className="mx-auto size-7 text-neon" />
              <p className="mt-4 font-display text-xl">La pista está esperando</p>
              <p className="mt-2 text-bone/50">Sé la primera persona en proponer una canción.</p>
            </div>
          ) : (
            <ol className="space-y-2">
              {songs.data?.map((song, index) => (
                <li
                  key={song.id}
                  className={`grid grid-cols-[2rem_1fr_auto] items-center gap-3 border p-3 transition-colors ${song.voted ? "border-neon bg-neon/8" : "border-bone/15 bg-bone/[0.03]"}`}
                >
                  <span className="font-mono text-xs text-neon">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-semibold">{song.title}</p>
                    <p className="truncate text-sm text-bone/45">
                      {song.artist || "Artista pendiente"}
                      {song.guest_alias ? ` · ${song.guest_alias}` : ""}
                    </p>
                  </div>
                  <button
                    className={`flex min-w-14 flex-col items-center border px-3 py-2 ${song.voted ? "border-neon bg-neon text-white" : "border-bone/20 text-bone hover:border-neon hover:text-neon"}`}
                    disabled={vote.isPending}
                    onClick={() => vote.mutate(song.id)}
                    aria-label={`${song.voted ? "Retirar voto de" : "Votar"} ${song.title}`}
                  >
                    <ArrowUp className="size-4" />
                    <strong className="font-mono text-xs">{song.vote_count}</strong>
                  </button>
                </li>
              ))}
            </ol>
          )}
        </section>
        <footer className="mt-16 border-t border-bone/10 pt-5 text-center font-mono text-[9px] uppercase tracking-[0.18em] text-bone/35">
          Sin cuentas · Sin spam · Una propuesta no garantiza que suene
        </footer>
      </div>
      {proposalOpen && (
        <ProposalModal
          onClose={() => setProposalOpen(false)}
          onSubmit={async (alias, title, artist) => {
            try {
              await guestRepository.propose(token, sessionToken, alias, title, artist);
              toast.success("Propuesta enviada para aprobación");
              setProposalOpen(false);
            } catch (error) {
              toast.error(error instanceof Error ? error.message : "No se pudo enviar");
            }
          }}
        />
      )}
    </main>
  );
}

function ProposalModal({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: (alias: string, title: string, artist: string) => Promise<void>;
}) {
  const [alias, setAlias] = useState("");
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [loading, setLoading] = useState(false);
  return (
    <Modal
      title="Propón una canción"
      description="Se publicará cuando SUSIEDAD la apruebe."
      onClose={onClose}
    >
      <form
        className="space-y-5"
        onSubmit={async (event) => {
          event.preventDefault();
          setLoading(true);
          await onSubmit(alias, title, artist);
          setLoading(false);
        }}
      >
        <label className="block">
          <span className="eyebrow mb-2 block text-foreground">Tu nombre o alias</span>
          <input
            className="field"
            maxLength={60}
            value={alias}
            onChange={(event) => setAlias(event.target.value)}
          />
        </label>
        <label className="block">
          <span className="eyebrow mb-2 block text-foreground">Canción*</span>
          <input
            className="field"
            required
            maxLength={150}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <label className="block">
          <span className="eyebrow mb-2 block text-foreground">Artista</span>
          <input
            className="field"
            maxLength={150}
            value={artist}
            onChange={(event) => setArtist(event.target.value)}
          />
        </label>
        <div className="flex justify-end gap-3">
          <button type="button" className="btn-quiet" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-primary" disabled={loading}>
            {loading ? "Enviando…" : "Enviar propuesta"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Expired() {
  return (
    <main className="grid min-h-screen place-items-center bg-onyx p-6 text-bone">
      <div className="max-w-md text-center">
        <BrandLogo variant="monogram" className="mx-auto h-14 w-auto" />
        <p className="eyebrow mt-8 !text-neon">Enlace no disponible</p>
        <h1 className="mt-4 font-display text-5xl leading-none">ESTA SESIÓN HA TERMINADO.</h1>
        <p className="mt-5 text-bone/55">
          El enlace ha caducado o ha sido revocado. Pide a la organización un QR actualizado.
        </p>
      </div>
    </main>
  );
}
